/** Company & BIR identity (M01-01–03, M01-07). */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Alert, Button, Card, Field, PageHeader, Skeleton, inputClass } from '../../components/ui';

export interface CompanyView {
  id: string;
  registeredName: string;
  tradeName: string;
  tin: string | null;
  tinBranchCode: string;
  tinFormatted: string | null;
  rdoCode: string;
  street: string;
  barangay: string;
  city: string;
  province: string;
  zip: string;
  lineOfBusiness: string;
  psicCode: string;
  registrationDate: string | null;
  casAcNumber: string;
  casAcDate: string | null;
  taxpayerClass: 'MICRO' | 'SMALL' | 'MEDIUM' | 'LARGE';
  vatStatus: 'VAT' | 'NON_VAT';
  vatStatusFrom: string | null;
  eightPercentOption: boolean;
  fiscalYearStartMonth: number;
  booksRegisteredOn: string | null;
  booksRegistrationRef: string;
  booksCovered: string;
  status: 'SETUP' | 'ACTIVE' | 'SUSPENDED';
  setupSteps: string[];
  pricesIncludeVat: boolean;
  ewtRecognition: 'ACCRUAL' | 'PAYMENT';
}

export const companyQuery = { queryKey: ['company'], queryFn: () => api<CompanyView>('/company') };

export function useCompany() {
  return useQuery(companyQuery);
}

type Form = Omit<CompanyView, 'id' | 'tinFormatted' | 'status' | 'setupSteps' | 'fiscalYearStartMonth'>;

export function CompanyPage(): JSX.Element {
  const { can } = useAuth();
  const company = useCompany();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);
  const editable = can('company.manage');

  useEffect(() => {
    if (!company.data) return;
    const { id: _i, tinFormatted: _t, status: _s, setupSteps: _st, fiscalYearStartMonth: _f, ...rest } = company.data;
    void _i; void _t; void _s; void _st; void _f;
    setForm({ ...rest, tin: rest.tin ?? '' });
  }, [company.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ...form };
      if (!form?.tin) delete body.tin;
      for (const k of ['registrationDate', 'casAcDate', 'booksRegisteredOn'] as const) if (body[k] === '') body[k] = null;
      if (form?.vatStatus === company.data?.vatStatus) delete body.vatStatusFrom;
      return api<CompanyView>('/company', { method: 'PATCH', body });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['company'], data);
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if (company.isLoading || !form) return <Skeleton className="h-96 w-full" />;
  const set = (patch: Partial<Form>): void => setForm((f) => (f ? { ...f, ...patch } : f));
  const text = (key: keyof Form, labelText: string, opts: { required?: boolean; hint?: string; max?: number } = {}) => (
    <Field label={labelText} required={opts.required} hint={opts.hint} error={error?.fields[key as string]}>
      <input className={inputClass} disabled={!editable} maxLength={opts.max} value={(form[key] as string) ?? ''} onChange={(e) => set({ [key]: e.target.value } as Partial<Form>)} />
    </Field>
  );
  const vatChanging = form.vatStatus !== company.data?.vatStatus;

  return (
    <>
      <PageHeader title="Company & BIR" description="These details print on every invoice and book, exactly as registered with the BIR." />
      {error && <div className="mb-4"><Alert tone="critical">{error.message}</Alert></div>}
      {saved && <div className="mb-4"><Alert tone="ok">Saved.</Alert></div>}
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        <Card title="Registration">
          <div className="grid gap-4 md:grid-cols-2">
            {text('registeredName', 'Registered name', { required: true, max: 200 })}
            {text('tradeName', 'Trade name / business style', { max: 200 })}
            {text('tin', 'TIN', { required: true, hint: '9 digits. Stored encrypted.' })}
            {text('tinBranchCode', 'Branch code', { required: true, hint: 'Head office is 00000.' })}
            {text('rdoCode', 'RDO code', { required: true, hint: 'e.g. 081 (Cebu City North)' })}
            <Field label="Registration date">
              <input type="date" className={inputClass} disabled={!editable} value={form.registrationDate ?? ''} onChange={(e) => set({ registrationDate: e.target.value })} />
            </Field>
            {text('lineOfBusiness', 'Line of business', { max: 200 })}
            {text('psicCode', 'PSIC code', { max: 20 })}
          </div>
        </Card>
        <Card title="Registered address">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">{text('street', 'Unit, building, street', { required: true, max: 200 })}</div>
            {text('barangay', 'Barangay', { max: 100 })}
            {text('city', 'City / municipality', { required: true, max: 100 })}
            {text('province', 'Province', { max: 100 })}
            {text('zip', 'ZIP code', { required: true, max: 4 })}
          </div>
        </Card>
        <Card title="Tax profile" description="Drives which returns are generated and how often.">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Taxpayer classification" hint="Per RR 8-2024 (Micro / Small / Medium / Large).">
              <select className={inputClass} disabled={!editable} value={form.taxpayerClass} onChange={(e) => set({ taxpayerClass: e.target.value as Form['taxpayerClass'] })}>
                <option value="MICRO">Micro</option>
                <option value="SMALL">Small</option>
                <option value="MEDIUM">Medium</option>
                <option value="LARGE">Large</option>
              </select>
            </Field>
            <Field label="VAT registration">
              <select className={inputClass} disabled={!editable} value={form.vatStatus} onChange={(e) => set({ vatStatus: e.target.value as Form['vatStatus'] })}>
                <option value="VAT">VAT-registered</option>
                <option value="NON_VAT">Non-VAT (percentage tax)</option>
              </select>
            </Field>
            {vatChanging && (
              <Field label="New VAT status takes effect on" required hint="Applies from this date forward only." error={error?.fields.vatStatusFrom}>
                <input type="date" className={inputClass} value={form.vatStatusFrom ?? ''} onChange={(e) => set({ vatStatusFrom: e.target.value })} />
              </Field>
            )}
            {form.vatStatus === 'NON_VAT' && (
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <input type="checkbox" className="h-4 w-4" disabled={!editable} checked={form.eightPercentOption} onChange={(e) => set({ eightPercentOption: e.target.checked })} />
                Sole proprietor under the 8% income tax option
              </label>
            )}
          </div>
        </Card>
        <Card title="Accounting preferences">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" disabled={!editable} checked={form.pricesIncludeVat} onChange={(e) => set({ pricesIncludeVat: e.target.checked })} />
              Sales prices are typed VAT-inclusive by default
            </label>
            <Field label="Expanded withholding tax is owed" hint="The BIR rule is the earlier of booking or payment. [Confirm with your tax adviser.]">
              <select className={inputClass} disabled={!editable} value={form.ewtRecognition} onChange={(e) => set({ ewtRecognition: e.target.value as Form['ewtRecognition'] })}>
                <option value="ACCRUAL">When the bill is booked (accrual)</option>
                <option value="PAYMENT">When the supplier is paid</option>
              </select>
            </Field>
          </div>
        </Card>
        <Card title="CAS & books registration" description="Fill these in once the BIR issues the Acknowledgment Certificate. The AC number prints on system-generated invoices.">
          <div className="grid gap-4 md:grid-cols-2">
            {text('casAcNumber', 'CAS Acknowledgment Certificate no.', { max: 60 })}
            <Field label="AC date issued">
              <input type="date" className={inputClass} disabled={!editable} value={form.casAcDate ?? ''} onChange={(e) => set({ casAcDate: e.target.value })} />
            </Field>
            <Field label="Books registered on">
              <input type="date" className={inputClass} disabled={!editable} value={form.booksRegisteredOn ?? ''} onChange={(e) => set({ booksRegisteredOn: e.target.value })} />
            </Field>
            {text('booksRegistrationRef', 'Books registration reference', { max: 100 })}
            <div className="md:col-span-2">{text('booksCovered', 'Books covered', { max: 300, hint: 'e.g. General Journal, General Ledger, Cash Receipts, Cash Disbursements, Sales, Purchases' })}</div>
          </div>
        </Card>
        {editable && (
          <div className="flex justify-end">
            <Button type="submit" busy={save.isPending}>Save changes</Button>
          </div>
        )}
      </form>
    </>
  );
}
